// manageThemisRuleEffects.ts
import axios, { AxiosResponse } from "axios";
import {
  ThemisConditionValueType,
  ThemisEffectOperation,
  ThemisFieldKeyEffectTarget,
} from "./ThemisInterfaces";

type IdResponseDTO = { id: number };

const baseURL = process.env.REACT_APP_API_URL;
const themisAdminBase = `${baseURL}/admin/themis`;

/**
 * Backend:
 * public enum ThemisEffectKindDto { Price=0, FieldKey=1, Display=2, Time=3 }
 * We only expose Price + FieldKey in UI.
 */
export enum ThemisEffectKind {
  Price = "Price",
  FieldKey = "FieldKey",
}

const kindToInt = (k: ThemisEffectKind): number => {
  switch (k) {
    case ThemisEffectKind.Price:
      return 0;
    case ThemisEffectKind.FieldKey:
      return 1;
    default:
      return 0;
  }
};

// Backend enum order is not pasted, but based on TS enum naming,

const operationToInt = (op: ThemisEffectOperation): number => {
  switch (op) {
    case ThemisEffectOperation.Addition:
      return 0;
    case ThemisEffectOperation.Subtraction:
      return 1;
    case ThemisEffectOperation.Multiplication:
      return 2;
    case ThemisEffectOperation.Set:
      return 3;
    case ThemisEffectOperation.Limit:
      return 4;
    case ThemisEffectOperation.Cap:
      return 5;
    default:
      return 0;
  }
};

// ConditionValueType backend order
const valueTypeToInt = (vt: ThemisConditionValueType): number => {
  switch (vt) {
    case ThemisConditionValueType.String:
      return 0;
    case ThemisConditionValueType.Number:
      return 1;
    case ThemisConditionValueType.Boolean:
      return 2;
    case ThemisConditionValueType.DateTimeUtcMs:
      return 3;
    case ThemisConditionValueType.Int:
      return 4;
    case ThemisConditionValueType.Decimal:
      return 5;
    default:
      return 0;
  }
};

// FieldKeyTarget backend has explicit values (0..5, 20..24). Map strings to those ints.
const fieldKeyTargetToInt = (t: ThemisFieldKeyEffectTarget): number => {
  switch (t) {
    case ThemisFieldKeyEffectTarget.LineSubtotal:
      return 0;
    case ThemisFieldKeyEffectTarget.LineDiscountTotal:
      return 1;
    case ThemisFieldKeyEffectTarget.LineFeeTotal:
      return 2;
    case ThemisFieldKeyEffectTarget.LineTaxRate:
      return 3;
    case ThemisFieldKeyEffectTarget.LineTaxAmount:
      return 4;
    case ThemisFieldKeyEffectTarget.LineEventNow:
      return 5;

    case ThemisFieldKeyEffectTarget.TargetDiscountTotal:
      return 20;
    case ThemisFieldKeyEffectTarget.TargetFeeTotal:
      return 21;
    case ThemisFieldKeyEffectTarget.TargetTaxRate:
      return 22;
    case ThemisFieldKeyEffectTarget.TargetTaxAmount:
      return 23;
    case ThemisFieldKeyEffectTarget.TargetEventNow:
      return 24;

    default:
      return 0;
  }
};

export type CreateEffectRequestDto = {
  kind: ThemisEffectKind; // UI string
  name: string;
  label: string;
  description: string;

  // phase is required on create, but rule phase should always be used
  phase: string;

  operation: ThemisEffectOperation; // UI string enum

  // Union payload:
  priceValue?: number | null;
  timeValue?: number | null;
  displayValue?: boolean | null;

  // FieldKey payload:
  fieldKeyValue?: string | null;
  fieldKeyValueType?: ThemisConditionValueType | null;
  fieldKeyTarget?: ThemisFieldKeyEffectTarget | null;
};

export type UpdateEffectRequestDto = Omit<CreateEffectRequestDto, "kind" | "phase">;

export function createEffect(
  ruleId: number,
  req: CreateEffectRequestDto
): Promise<AxiosResponse<IdResponseDTO>> {
  return axios.post(`${themisAdminBase}/rules/${ruleId}/effects`, {
    kind: kindToInt(req.kind),
    name: req.name,
    label: req.label,
    description: req.description,
    phase: req.phase,
    operation: operationToInt(req.operation),

    priceValue: req.priceValue ?? null,
    timeValue: req.timeValue ?? null,
    displayValue: req.displayValue ?? null,

    fieldKeyValue: req.fieldKeyValue ?? null,
    fieldKeyValueType:
      req.fieldKeyValueType != null ? valueTypeToInt(req.fieldKeyValueType) : null,
    fieldKeyTarget:
      req.fieldKeyTarget != null ? fieldKeyTargetToInt(req.fieldKeyTarget) : null,
  });
}

export function updateEffect(
  id: number,
  req: UpdateEffectRequestDto
): Promise<AxiosResponse<void>> {
  return axios.put(`${themisAdminBase}/effects/${id}`, {
    name: req.name,
    label: req.label,
    description: req.description,
    operation: operationToInt(req.operation),

    priceValue: req.priceValue ?? null,
    timeValue: req.timeValue ?? null,
    displayValue: req.displayValue ?? null,

    fieldKeyValue: req.fieldKeyValue ?? null,
    fieldKeyValueType:
      req.fieldKeyValueType != null ? valueTypeToInt(req.fieldKeyValueType) : null,
    fieldKeyTarget:
      req.fieldKeyTarget != null ? fieldKeyTargetToInt(req.fieldKeyTarget) : null,
  });
}

export function deleteEffect(id: number): Promise<AxiosResponse<void>> {
  return axios.delete(`${themisAdminBase}/effects/${id}`);
}
